import {useCallback, useLayoutEffect, useState} from 'react';

/*
 * The height, in pixels, that lets an element run from where it sits down to
 * the bottom of the viewport: the editors on the Scene, Vibe Code and Blocks
 * tabs size themselves this way so their tool panes and button rows stay on
 * screen without the page scrolling.
 *
 * It measures the element's own top edge rather than adding up the heights of
 * the header and tab bar above it, which broke silently when the project
 * header was rebuilt and its class name changed. Re-measured when the window
 * resizes and whenever the element's size changes, which also covers a tab
 * pane that mounts hidden and is shown later.
 *
 * `ref` is a callback ref, so an element that mounts after a loading state
 * (the Blocks editor shows a spinner first) is still picked up. `height` is
 * null until measured, so callers can fall back to a CSS height.
 */
export default function useFillViewportHeight(bottomGap = 0): {
    ref: (element: HTMLElement | null) => void;
    height: number | null;
} {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const [height, setHeight] = useState<number | null>(null);

    const ref = useCallback((node: HTMLElement | null) => setElement(node), []);

    useLayoutEffect(() => {
        if (!element) {
            return;
        }

        const measure = () => {
            const top = element.getBoundingClientRect().top;

            // A hidden pane (display: none) measures at 0,0 - leave it alone.
            if (element.offsetParent === null && top === 0) {
                return;
            }

            const viewportHeight =
                window.visualViewport?.height ?? window.innerHeight;
            const next = Math.max(
                0,
                Math.round(viewportHeight - top - bottomGap)
            );

            setHeight(previous => (previous === next ? previous : next));
        };

        measure();

        window.addEventListener('resize', measure);
        window.visualViewport?.addEventListener('resize', measure);

        const observer = new ResizeObserver(() => measure());
        observer.observe(element);

        return () => {
            window.removeEventListener('resize', measure);
            window.visualViewport?.removeEventListener('resize', measure);
            observer.disconnect();
        };
    }, [element, bottomGap]);

    return {ref, height};
}
